import { Box, Button, Grid, ThemeProvider } from '@mui/material';
import { DocumentType, Media, UploadState, UploadType } from '@synergy/models';
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik';
import { FormattedDate, FormattedMessage } from 'react-intl';
import {
    Localized,
    LocalizedHtml,
    PoolDropzone,
    PoolFileList,
    PoolTextArea,
    ScrollToFieldError,
} from '@synergy/components';
import { PaloDetailView, PaloFormSection, PaloSideRail, PaloStatusPill } from '@synergy/components-palo';
import { PoolBanner } from '@synergy/components-next';
import { ReactElement, useEffect, useState } from 'react';
import { base64encode, getDocumentStatusTranslationId, getLocalizedString } from '@synergy/utils';
import { customerMediaSelector, removeUploadedFile, setUploads } from '@synergy/data-access';
import { theme as themeNext } from '@synergy/theme-next';
import { tokens } from '@synergy/theme-palo';
import { useNavigate, useParams } from 'react-router';

import { DemandDeclineModal } from '../components/demand-decline-modal';
import { DemandDetail } from '../models/demand-detail';
import { DemandDetailAccountItem } from '../models/demand-detail-account-item';
import { DemandDetailFormValues } from '../models/demand-detail-form-values';
import { DemandSubmitModal } from '../components/demand-submit-modal';
import { DemandsDetailAccordionTable } from '../components/demands-detail-accordion-table';
import { DemandsDetailItemTable } from '../components/demands-detail-item-table';
import { PaloDemandsDetailDock } from './palo-demands-detail-dock';
import { declineDemand } from '../../store/demands-actions';
import { demandsDetailValidationSchema } from '../demands-detail-validation-schema';
import { fetchDemand, sendDemandAccount, updateSessionStorage } from '../store/demands-detail-actions';
import { localesSelector } from '../../../../common/selectors/locale-selectors';
import { useSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const c = tokens.color;

export const PaloDemandsDetail = (): ReactElement | null => {
    const { id: localId } = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const dispatch = useThunkDispatch();

    const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
    const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
    const [noDemandItemIds, setNoDemandItemIds] = useState<number[]>([]);

    const { demand, readonly } = useSelector(state => state.demands.detail);
    const mediaBaseUrl = useSelector(state => state.settings.mediaBaseUrl);
    const customerMedia = useSelector(customerMediaSelector);
    const locales = useSelector(localesSelector);
    // Which company this report speaks for. A contact who looks after two of
    // them cannot tell otherwise (#2215, R-71).
    const accountName = useSelector(state => state.account?.name);

    useEffect(() => {
        if (localId) {
            dispatch(fetchDemand(Number.parseInt(localId, 10)));
        }
    }, [dispatch, localId]);

    useEffect(() => {
        if (demand?.currentDemandAccount?.media) {
            dispatch(setUploads(demand.currentDemandAccount.media));
        }
    }, [demand, dispatch]);

    if (!demand) {
        return null;
    }

    const initialValues = (initial: DemandDetail): DemandDetailFormValues => ({
        id: initial.currentDemandAccount?.id ?? -1,
        demandAccountItems:
            initial.currentDemandAccount?.demandAccountItems.map<DemandDetailAccountItem>(item => ({
                id: item.id ?? -1,
                deliveryAddress: item?.deliveryAddress,
                quantity: item.quantity,
                demandItemId: item.demandItemId,
                noDemand: item.noDemand ?? false,
            })) ?? [],
        noteFromCustomer: initial.currentDemandAccount?.noteFromCustomer ?? '',
        declineReason: initial.currentDemandAccount?.declineReason ?? '',
        files: initial.currentDemandAccount?.media ?? [],
    });

    const canSubmit = noDemandItemIds.length !== demand.demandItems.length;

    /**
     * Nothing is needed anywhere: then saying so is the step that is left, and
     * it takes the main action's place (#2215, R-72). Reporting a demand stays
     * where it is, disabled, so the way back is visible.
     */
    const noDemandAtAll = !canSubmit && demand.demandItems.length > 0;

    const backToOverview = (formikProps: FormikProps<DemandDetailFormValues>): void => {
        dispatch(updateSessionStorage(formikProps.values));
        navigate('/');
    };

    const handleFileDrop = async (
        acceptedFiles: File[],
        form: FormikProps<DemandDetailFormValues>,
        name: string
    ): Promise<void> => {
        const deduplicated = acceptedFiles.filter(
            file => !customerMedia.some(m => m.originalName === file.name && m.size === file.size)
        );

        if (0 === deduplicated.length) {
            return;
        }

        const added = await Promise.all(
            deduplicated.map(async file => Media.fromFile(file, UploadType.Customer, await base64encode(file)))
        );
        form.setFieldValue(name, [...form.values.files, ...added]);
    };

    const handleFileRemove = (media: Media, form: FormikProps<DemandDetailFormValues>, name: string): void => {
        if (demand.id) {
            dispatch(removeUploadedFile(DocumentType.Demand, demand.id, media));
        }

        form.setFieldValue(
            name,
            form.values.files.filter(file => !(file.id === media.id && file.originalName === media.originalName))
        );
    };

    const handleNoDemandChecked = (demandItemId: number, checked: boolean): void => {
        setNoDemandItemIds(
            checked
                ? [...new Set([...noDemandItemIds, demandItemId])]
                : noDemandItemIds.filter(id => id !== demandItemId)
        );
    };

    const confirmSubmit = async (formikProps: FormikProps<DemandDetailFormValues>): Promise<void> => {
        const values = formikProps.values;

        values.demandAccountItems = values.demandAccountItems.map(item => {
            const none = noDemandItemIds.includes(item.demandItemId);

            return { ...item, noDemand: none, quantity: none ? 0 : item.quantity };
        });

        if (values.demandAccountItems.length > 0) {
            await dispatch(sendDemandAccount(values));
            dispatch(updateSessionStorage(values));
            setIsSubmitModalOpen(false);
            navigate('/');
        }
    };

    const confirmDecline = async (declineReason: string): Promise<void> => {
        setIsDeclineModalOpen(false);

        if (!demand.currentDemandAccount) {
            return;
        }

        await dispatch(declineDemand(demand.currentDemandAccount.id, declineReason));
        navigate('/');
    };

    return (
        <Formik
            initialValues={initialValues(demand)}
            onSubmit={() => setIsSubmitModalOpen(true)}
            enableReinitialize={false}
            validationSchema={demandsDetailValidationSchema(noDemandItemIds)}
        >
            {(formikProps: FormikProps<DemandDetailFormValues>) => (
                <>
                    <ScrollToFieldError />
                    <Form onSubmit={formikProps.handleSubmit} style={{ height: '100%' }}>
                        <PaloDetailView
                            onBack={() => backToOverview(formikProps)}
                            number={demand.number}
                            title={getLocalizedString(locales, demand.title)}
                            status={
                                <PaloStatusPill
                                    status={demand.status}
                                    label={<FormattedMessage id={getDocumentStatusTranslationId(demand.status)} />}
                                />
                            }
                            meta={[
                                ...(accountName
                                    ? [
                                          {
                                              label: <FormattedMessage id="app.common.account" />,
                                              value: accountName,
                                          },
                                      ]
                                    : []),
                                {
                                    label: <FormattedMessage id="app.table.column.endDate" />,
                                    value: (
                                        <FormattedDate
                                            value={demand.endDate}
                                            year="numeric"
                                            month="short"
                                            day="2-digit"
                                        />
                                    ),
                                },
                            ]}
                            actions={
                                <>
                                    {noDemandAtAll && (
                                        <Button variant="contained" color="primary" type="submit" disabled={true}>
                                            <FormattedMessage id="app.demands.detail.submit" />
                                        </Button>
                                    )}
                                    <Button
                                        data-testid="demand-decline"
                                        variant={noDemandAtAll ? 'contained' : 'outlined'}
                                        color={noDemandAtAll ? 'error' : 'secondary'}
                                        disabled={readonly}
                                        onClick={() => setIsDeclineModalOpen(true)}
                                    >
                                        <FormattedMessage id="app.demands.detail.sendNoDemand" />
                                    </Button>
                                    {!noDemandAtAll && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            disabled={readonly || !canSubmit}
                                            onClick={() => formikProps.submitForm()}
                                        >
                                            <FormattedMessage id="app.demands.detail.submit" />
                                        </Button>
                                    )}
                                </>
                            }
                            dock={
                                <PaloSideRail
                                    sections={[
                                        {
                                            key: 'details',
                                            label: <FormattedMessage id="app.common.details" />,
                                            content: <PaloDemandsDetailDock demand={demand} />,
                                        },
                                    ]}
                                    collapsedLabel={<FormattedMessage id="app.common.details" />}
                                />
                            }
                        >
                            <Box id="scroll-container" sx={{ height: '100%', overflowY: 'auto', padding: '14px 16px' }}>
                                {demand.demandItems.map((demandItem, index) => {
                                    const submitted = demandItem.submitted.reduce(
                                        (total, entry) =>
                                            total + entry.items.reduce((sum, item) => sum + item.quantity, 0),
                                        0
                                    );

                                    return (
                                        <Box component="section" id={`demand-${demandItem.id}`} key={demandItem.id}>
                                            <PaloFormSection title={<Localized value={demandItem.name} />}>
                                                {demandItem.contentQuantity && (
                                                    <Box
                                                        sx={{ fontSize: '12.5px', color: c.ink2, marginBottom: '10px' }}
                                                    >
                                                        <FormattedMessage
                                                            id="app.demands.detail.contentQuantity"
                                                            values={{
                                                                value: demandItem.contentQuantity,
                                                                unit: demandItem.contentUnit,
                                                                orderUnit: demandItem.orderUnit,
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                                {demandItem.longDescription && (
                                                    <Box sx={{ marginBottom: '12px' }}>
                                                        {/* Folded, as the legacy accordion had it: these
                                                            descriptions run long, and open they push the
                                                            quantity fields off the screen. */}
                                                        <PaloFormSection
                                                            title={
                                                                <FormattedMessage id="app.demands.detail.productDescription" />
                                                            }
                                                            defaultOpen={false}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    fontSize: '12.5px',
                                                                    color: c.ink2,
                                                                    '& p': { marginTop: 0, marginBottom: '7px' },
                                                                }}
                                                            >
                                                                <LocalizedHtml value={demandItem.longDescription} />
                                                            </Box>
                                                        </PaloFormSection>
                                                    </Box>
                                                )}

                                                {/* Read only, the survey shows what was reported
                                                    rather than a form to fill in (#2287). */}
                                                {!readonly && (
                                                    <DemandsDetailItemTable
                                                        demand={demand}
                                                        demandItem={demandItem}
                                                        formikProps={formikProps}
                                                        index={index}
                                                        handleNoDemandChecked={handleNoDemandChecked}
                                                    />
                                                )}
                                                {readonly && 0 === demandItem.submitted.length && (
                                                    <Box sx={{ fontSize: '12.5px', color: c.ink3 }}>
                                                        <FormattedMessage id="app.demands.detail.noSubmission" />
                                                    </Box>
                                                )}

                                                {submitted > 0 && demand.currentDemandAccount && (
                                                    <PoolBanner
                                                        variant="outlined"
                                                        sx={{ mt: 2, mb: 1, width: 'fit-content' }}
                                                    >
                                                        <FormattedMessage
                                                            id="app.demands.detail.totalSubmitted"
                                                            values={{
                                                                totalQuantity: submitted,
                                                                orderUnit: demandItem.orderUnit,
                                                                customer:
                                                                    demand.currentDemandAccount.customerAccount.name,
                                                            }}
                                                        />
                                                    </PoolBanner>
                                                )}

                                                <DemandsDetailAccordionTable demandItem={demandItem} />
                                            </PaloFormSection>
                                        </Box>
                                    );
                                })}

                                <Box component="section" id="noteFromCustomer">
                                    <PaloFormSection title={<FormattedMessage id="app.common.note" />}>
                                        <Box sx={{ fontSize: '12.5px', color: c.ink2, marginBottom: '12px' }}>
                                            <FormattedMessage id="app.common.noteDetails" />
                                        </Box>
                                        {/* Both columns end on the same line: the upload area
                                            grows with the remark beside it instead of standing on
                                            a fixed height of its own (#2215, R-70). */}
                                        <Grid container={true} spacing={2} alignItems="stretch">
                                            {/* Takes the height of the column and carries no label of its
                                                own — "Bemerkung" is the heading right above, and a second
                                                one pushed the box below the upload area (#2215, R-70). */}
                                            <Grid
                                                item={true}
                                                lg={7}
                                                xs={12}
                                                sx={{
                                                    display: 'flex',
                                                    '& > div': { width: '100%' },
                                                    '& .MuiFormControl-root': { height: '100%' },
                                                    '& .MuiInputBase-root': {
                                                        height: '100%',
                                                        alignItems: 'flex-start',
                                                    },
                                                }}
                                            >
                                                <Field name="noteFromCustomer">
                                                    {({ field }: FieldProps<string>) => (
                                                        <PoolTextArea
                                                            id="noteFromCustomer"
                                                            type="text"
                                                            rows="5"
                                                            rowsMax="50"
                                                            {...field}
                                                            readOnly={readonly}
                                                        />
                                                    )}
                                                </Field>
                                            </Grid>
                                            {/* A plain flex column: Grid's spacing works with negative
                                                margins and made this column taller than its row. */}
                                            <Grid item={true} lg={5} xs={12}>
                                                <Field name="files">
                                                    {({ field, form }: FieldProps<Media[]>) => (
                                                        <Box
                                                            sx={{
                                                                height: '100%',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '16px',
                                                            }}
                                                        >
                                                            <Box sx={{ flex: 1, display: 'flex' }}>
                                                                <PoolDropzone
                                                                    onDrop={files =>
                                                                        handleFileDrop(files, form, field.name)
                                                                    }
                                                                    disabled={readonly}
                                                                />
                                                            </Box>
                                                            {/* No wrapper: an empty list must take no row of its
                                                                own, or the gap below the upload area stays. */}
                                                            <PoolFileList
                                                                acceptedFiles={formikProps.values.files.map(media =>
                                                                    media.state
                                                                        ? media
                                                                        : { ...media, state: UploadState.Finished }
                                                                )}
                                                                downloadBaseUrl={mediaBaseUrl}
                                                                onCancelClicked={media => media.cancel?.()}
                                                                onDeleteClicked={media =>
                                                                    handleFileRemove(media, form, field.name)
                                                                }
                                                                readOnly={readonly}
                                                            />
                                                        </Box>
                                                    )}
                                                </Field>
                                            </Grid>
                                        </Grid>
                                    </PaloFormSection>
                                </Box>
                            </Box>
                        </PaloDetailView>
                    </Form>

                    <ThemeProvider theme={themeNext}>
                        <DemandSubmitModal
                            open={isSubmitModalOpen}
                            demandTitle={getLocalizedString(locales, demand.title)}
                            endDate={demand.endDate}
                            onCancel={() => setIsSubmitModalOpen(false)}
                            onConfirm={() => confirmSubmit(formikProps)}
                        />
                        <DemandDeclineModal
                            open={isDeclineModalOpen}
                            onConfirm={confirmDecline}
                            onCancel={() => setIsDeclineModalOpen(false)}
                        />
                    </ThemeProvider>
                </>
            )}
        </Formik>
    );
};
